import React, { memo, type FC, useState, useEffect, useRef } from 'react';
import { Search } from '@wix/wix-base-ui';
import { hoc } from '@/util';

import type { ISuggestResult, ContentManager } from '@/presetApi';
import type { BusinessType } from '@/siteGlobalData';

import { getBusinessTypeByString, suggestItemsToOptions } from './utils';
import {
  type DispatchProps,
  mapDispatchToProps,
} from './businessTypeInputMapper';

export interface OwnProps {
  value: BusinessType;
  placeholder: string;
  className?: string;
  clearButton?: boolean;
  onChange: any;
  onBlur?: any;
  onFocus?: any;
}

export interface BusinessTypeInputProps extends OwnProps, DispatchProps {}

const BusinessTypeInput: FC<BusinessTypeInputProps> = memo(
  function BusinessTypeInput({
    value,
    placeholder,
    className,
    clearButton,
    getContentManager,
    initiateContentManager,
    onChange,
    onBlur,
    onFocus,
  }) {
    const contentManager = useRef<ContentManager>(getContentManager());
    const request = useRef<Promise<void>>();

    const [inputSuggestions, setInputSuggestions] = useState<
      Record<string, ISuggestResult>
    >({});
    const [currentInputValue, setCurrentInputValue] = useState<string>(
      value?.displayName,
    );

    useEffect(() => {
      const initializeContentManager = async () => {
        if (contentManager.current === undefined) {
          contentManager.current = null;
          await initiateContentManager();
          contentManager.current = getContentManager();
        }
      };

      initializeContentManager();
    }, [getContentManager, initiateContentManager]);

    useEffect(() => {
      const inputSuggestionsKey = currentInputValue.toLowerCase();
      if (!inputSuggestions[inputSuggestionsKey]) {
        const promise = contentManager.current
          ?.suggest(inputSuggestionsKey)
          ?.then((suggestions: ISuggestResult) => {
            if (request.current === promise && inputSuggestionsKey) {
              setInputSuggestions((state) => ({
                ...state,
                [inputSuggestionsKey]: suggestions,
              }));
            }
          });
        request.current = promise;
      }

      changeBusinessType(inputSuggestionsKey);

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentInputValue, inputSuggestions]);

    const changeBusinessType = (inputSuggestionsKey: string) => {
      const businessType = getBusinessTypeByString(
        currentInputValue,
        inputSuggestions[inputSuggestionsKey],
      );

      onChange(businessType);
    };

    const handleSuggest = (inputValue: string) => {
      setCurrentInputValue(inputValue);
    };

    const handleChange = (inputValue: string) => {
      setCurrentInputValue(inputValue);
    };

    return (
      <Search
        automationId="ai-injection-into-template-business-type"
        className={className}
        value={value?.displayName}
        options={suggestItemsToOptions(inputSuggestions[value?.displayName])}
        onClear={onChange}
        clearButton={clearButton}
        placeholder={placeholder}
        onChange={handleChange}
        onEnter={handleSuggest}
        onItemClick={handleSuggest}
        onFocus={onFocus}
        onBlur={onBlur}
      />
    );
  },
);

export default hoc.connect(
  hoc.STORES.EDITOR_API,
  null,
  mapDispatchToProps,
)(BusinessTypeInput);
